'use client'

import { useT, useFormat } from '@/lib/i18n'

/**
 * Stage 37 — the scenarios 6–7 case card (presentational).
 *
 * The previous version rendered the candidate catalog: identity + source uids, three
 * scores side by side, every mapped leg field with its null-cause, pairing evidence
 * (ranks, candidate counts, role), raw SHAP contributions, a data-limitations list and a
 * competition cluster. An analyst had to translate all of it.
 *
 * This version answers four questions — why look at this, why is it unusual, why might the
 * two shipments be linked, what should be checked. Everything technical (ids, model
 * versions, SHAP values, match score, ranks, counts, cluster, source field statuses,
 * catalog limitations) lives in the single collapsed «Технические сведения» block.
 *
 * SECTION ORDER (aligned with scenarios 1–5). The card used to open on the scores and
 * close on the facts, so an analyst met «9.99 из 10» before knowing what the case even
 * was. The reading order is now the same on both surfaces: facts → the physical hand-over
 * → why the two shipments are treated as one movement → how unusual it is → what exactly
 * is unusual, with numbers → what to check → competing links → provenance.
 *
 * ALL text and every hide/show decision comes from `cross-unit.case-view.ts`; this file
 * only lays the result out, which is why the guarantees are unit-testable.
 *
 * Section layout, cards and rows are the shared scenarios 1–5 primitives, so the two
 * drawers read as one product.
 */
import Link from 'next/link'
import { cn } from '@/lib/utils'
import { AnomalyEvidenceTable } from '../../components/anomaly-evidence-table'
import { EvidenceExamplesPanel } from './evidence-examples'
import { Collapsible, EtsngClassificationRows, Fact, Metric, Section } from '../../components/case-drawer-primitives'
import { buildCrossUnitCaseView, type CaseFact, type CaseLeg } from '../cross-unit.case-view'
import type { EtsngCaseClassification } from '@/lib/etsng-classification'
import type { CandidateExplanationsData, CandidatePairDetailData } from '../cross-unit.types'
import { CandidateError } from './cross-unit-primitives'

/** Anomaly level → colour, same mapping the scenarios 1–5 drawer uses. */
const ANOMALY_LEVEL_TEXT: Record<string, string> = {
  priority_review: 'text-risk-high',
  high: 'text-risk-high',
  elevated: 'text-risk-medium',
  baseline: 'text-risk-low',
}

/**
 * Drawer header. Names the case and carries a short id; the full `canonical_pair_id` and
 * both source identities are technical details and are rendered at the bottom instead.
 */
export function CandidatePairHeader({
  pairId,
  scenarioId,
  fullCaseHref,
  onClose,
}: {
  pairId: string
  scenarioId: number
  fullCaseHref?: string
  onClose: () => void
}) {
  const t = useT()
  return (
    <header
      data-block="drawer-header"
      className="sticky top-0 z-10 flex shrink-0 items-center justify-between gap-3 border-b border-[var(--border-default)] bg-[var(--bg-surface)] px-5 py-3"
    >
      <div className="min-w-0">
        <h2 className="text-[14px] font-semibold text-[var(--text-primary)]">Кейс · Сценарий {scenarioId}</h2>
        <p className="truncate font-mono text-[11px] text-[var(--text-tertiary)]">#{pairId.slice(0, 8)}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {fullCaseHref && (
          <Link
            href={fullCaseHref}
            className="rounded-[3px] border border-[var(--border-default)] px-2 py-1 text-[11px] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]"
          >
            {t('analytics.cu.openFullCase')}
          </Link>
        )}
        <button
          type="button"
          onClick={onClose}
          className="rounded-[3px] px-2 py-1 text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]"
        >
          ✕
        </button>
      </div>
    </header>
  )
}

/** One shipment column of «Стыковка перевозок». Rows are pre-filtered — no empty lines. */
function LegColumn({ leg }: { leg: CaseLeg }) {
  return (
    <div className="rounded-[3px] bg-[var(--bg-subtle)] px-2.5 py-2">
      <div className="text-[10px] uppercase tracking-[0.04em] text-[var(--text-tertiary)]">{leg.title}</div>
      {leg.rows.map((r) => (
        <Fact key={r.key} k={r.label} v={r.value} />
      ))}
    </div>
  )
}

/**
 * Классификация ЕТСНГ пары.
 *
 * Коды ног совпали — один блок с подзаголовком. Разошлись — по блоку на ногу, каждый
 * подписан стороной; общего заголовка нет намеренно, иначе две разные классификации
 * читались бы как одна. Классифицировать нечего — не рисуется ничего.
 */
function EtsngClassificationBlock({ view }: { view: EtsngCaseClassification }) {
  if (view.kind === 'shared') {
    return (
      <dl className="grid grid-cols-1 gap-1 text-[12px]">
        <EtsngClassificationRows classification={view.classification} heading="Классификация ЕТСНГ" />
      </dl>
    )
  }
  if (view.kind === 'perLeg') {
    return (
      <dl className="grid grid-cols-1 gap-1 text-[12px]">
        <EtsngClassificationRows classification={view.import} side="импорта" />
        <EtsngClassificationRows classification={view.export} side="экспорта" />
      </dl>
    )
  }
  return null
}

function FactList({ facts }: { facts: CaseFact[] }) {
  return (
    <dl className="grid grid-cols-1 gap-1 text-[12px]">
      {facts.map((f) => (
        <Fact key={f.key} k={f.label} v={f.value} />
      ))}
    </dl>
  )
}

export function CandidatePairDetailBody({
  detail,
  explanations,
  scenarioId,
  relatedSlot,
}: {
  detail: CandidatePairDetailData
  explanations?: CandidateExplanationsData | null
  scenarioId?: number
  /** «Другие возможные связи» — injected so the drawer owns paging and the page can omit it. */
  relatedSlot?: React.ReactNode
}) {
  const t = useT()
  const f = useFormat()
  const view = buildCrossUnitCaseView(f, t, detail, explanations, scenarioId)

  return (
    <div data-block="candidate-pair-detail" className="space-y-5">
      {/* ONE caveat for the whole card. The catalog's full candidate semantics are in
          «Технические сведения» — repeating them per section is what made the old card
          read as four warnings about the same thing. */}
      <p
        data-block="notice"
        className="rounded-[3px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] px-3 py-2.5 text-[12px] leading-relaxed text-[var(--text-secondary)]"
      >
        {view.notice}
      </p>

      {/* 1 — the raw facts: what this case IS, before any score (same order as 1–5) */}
      <Section title={t('analytics.caseDrawer.title')}>
        <FactList facts={view.details} />
        {/* Классификация ЕТСНГ — справочные уровни над кодом груза из замороженного
            снимка НСИ. Стоит сразу под фактами, тем же `Fact`, что и всё выше: это
            справка о коде, а не ещё один признак кейса. На балл, уровень, причины и
            доказательства не влияет и в них не входит. */}
        <EtsngClassificationBlock view={view.cargoClassification} />
        {view.dataQuality.length > 0 && (
          <div data-block="data-quality" className="mt-2 space-y-1">
            {view.dataQuality.map((w) => (
              <p key={w} className="rounded-[3px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] px-2.5 py-1.5 text-[11px] leading-relaxed text-[var(--text-secondary)]">
                {w}
              </p>
            ))}
          </div>
        )}
      </Section>

      {/* 2 — the physical hand-over */}
      <Section title={t('analytics.legs.title')}>
        <div className="space-y-2 text-[12px]">
          <div className="grid gap-2 md:grid-cols-2">
            <LegColumn leg={view.legs.importLeg} />
            <LegColumn leg={view.legs.exportLeg} />
          </div>
          {view.legs.middle.length > 0 && (
            <div className="rounded-[3px] bg-[var(--bg-subtle)] px-2.5 py-2">
              <div className="text-[10px] uppercase tracking-[0.04em] text-[var(--text-tertiary)]">{t('analytics.legs.innerJunction')}</div>
              {view.legs.middle.map((f) => (
                <Fact key={f.key} k={f.label} v={f.value} />
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* 3 — the main block for 6–7: the actual agreements between the two shipments.
          Titled exactly as on the scenarios 1–5 drawer — it answers the same question,
          and two names for one section made the surfaces read as two products. */}
      <Section title={t('analytics.linkage.title')}>
        <ul className="space-y-1.5">
          {view.linkage.items.map((i) => (
            <li key={i.key} data-linkage={i.key} data-tone={i.tone} className="flex gap-2 text-[12px] leading-relaxed">
              <span className={cn('shrink-0', i.tone === 'match' ? 'text-risk-low' : 'text-[var(--text-tertiary)]')}>
                {i.tone === 'match' ? '✓' : '·'}
              </span>
              <span className={i.tone === 'match' ? 'text-[var(--text-secondary)]' : 'text-[var(--text-tertiary)]'}>
                {i.text}
              </span>
            </li>
          ))}
        </ul>
        {view.linkage.strengthNote && (
          <p className="mt-2 text-[11px] leading-relaxed text-[var(--text-tertiary)]">{view.linkage.strengthNote}</p>
        )}
      </Section>

      {/* 4 — why look at this at all */}
      <Section title={t('analytics.caseDrawer.whyAttention')}>
        <p className="text-[12px] leading-relaxed text-[var(--text-secondary)]">{view.attention.summary}</p>
        {(view.attention.anomaly || view.attention.ml) && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            {view.attention.anomaly && (
              <Metric
                label={view.attention.anomaly.label}
                value={view.attention.anomaly.value}
                accent={ANOMALY_LEVEL_TEXT[detail.anomaly?.level ?? '']}
                hint={view.attention.anomaly.levelLabel}
              />
            )}
            {view.attention.ml && (
              <Metric
                label={view.attention.ml.label}
                value={view.attention.ml.value}
                hint={view.attention.ml.levelLabel}
              />
            )}
          </div>
        )}
      </Section>

      {/* 5 — what the anomaly model flagged, in plain words and without invented numbers */}
      <Section title={t('analytics.caseDrawer.whyUnusual')}>
        {view.unusual.emptyNote ? (
          <p className="text-[12px] leading-relaxed text-[var(--text-secondary)]">{view.unusual.emptyNote}</p>
        ) : (
          <ul className="space-y-2">
            {view.unusual.reasons.map((r) => (
              <li
                key={r.code}
                data-reason-code={r.code}
                className="rounded-[3px] border-l-2 border-l-[var(--accent-data)] bg-[var(--bg-subtle)] px-3 py-2"
              >
                <div className="text-[12px] font-medium text-[var(--text-primary)]">{r.title}</div>
                <p className="mt-1 text-[12px] leading-relaxed text-[var(--text-secondary)]">{r.text}</p>
                {/* Stage 39: the comparison moved out of the sentence into a table — the
                    same component the scenarios 1–5 drawer renders. */}
                {/* Stage 40: a counted row can be opened into the corpus rows behind it.
                    The panel is mounted only on click, so opening the drawer fetches
                    nothing extra. */}
                <AnomalyEvidenceTable
                  rows={r.evidence}
                  note={r.note}
                  renderExamples={(d) => (
                    <EvidenceExamplesPanel pairId={detail.identity?.canonicalPairId ?? ''} drilldown={d} />
                  )}
                />
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* 6 — checklist built from this case's own facts */}
      <Section title={t('analytics.caseDrawer.checklist')}>
        <ul className="space-y-1.5">
          {/* параметр стрелки назван line, а не t: в этом компоненте
              t — переводчик из useT(), и прежнее имя его затеняло. */}
          {view.checklist.map((line, i) => (
            <li key={i} className="flex gap-2 text-[11px] text-[var(--text-secondary)]">
              <span className="text-[var(--text-tertiary)]">☐</span>
              {line}
            </li>
          ))}
        </ul>
      </Section>

      {/* Stage 38: «Что повлияло на приоритет проверки» is gone.
          It was a simplified SHAP view of the ML priority score — a second model's
          feature attributions, on a card that answers «почему этот кейс необычен».
          The raw SHAP factors, their contributions, the base value and the model id
          are unchanged and remain in «Технические сведения». */}

      {/* 7 — competing candidates, collapsed and phrased for a reader */}
      {relatedSlot}

      {/* 8 — everything technical, collapsed, in one place */}
      <Collapsible title={t('analytics.pair.techDetails')} block="technical-details">
        <dl className="space-y-1 font-mono text-[10px] text-[var(--text-tertiary)]">
          {view.technical.map((f) => (
            <div key={f.key} className="flex gap-2">
              <dt className="shrink-0 text-[var(--text-tertiary)]">{f.label}:</dt>
              <dd className="break-all text-[var(--text-secondary)]">{f.value}</dd>
            </div>
          ))}
        </dl>
      </Collapsible>
    </div>
  )
}

export { CandidateError }
